import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useBrandOnboarding } from "@/context/BrandOnboardingContextFixed";
import { useAuthStore } from "@/stores/authStore";
import { useDebouncedNavigation } from "@/hooks/use-debounced-navigation";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import logo from '../../assets/images/base.png';

const companyInfoSchema = z.object({
  company_name: z.string().min(2, "Company name is required").max(100, "Company name must be 100 characters or less"),
  company_type: z.enum(["direct", "agency"]),
  agency_name: z.string().max(100, "Agency name must be 100 characters or less").optional(),
  website: z.string().url("Please enter a valid URL"),
  industry_category: z.string().min(1, "Industry category is required"),
}).refine((data) => {
  if (data.company_type === "agency" && !data.agency_name) {
    return false;
  }
  return true;
}, {
  message: "Agency name is required when company type is Agency",
  path: ["agency_name"],
});

type CompanyInfoFormValues = z.infer<typeof companyInfoSchema>;

// IAB Categories simplified list
const INDUSTRIES = [
  "Automotive",
  "Business & Finance",
  "Education",
  "Entertainment",
  "Food & Drink",
  "Health & Fitness",
  "Home & Garden",
  "Lifestyle",
  "News & Politics",
  "Science & Technology",
  "Shopping",
  "Sports",
  "Travel",
  "Other"
];

export default function BrandOnboardingStep1Fixed() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { status, isLoading: isStatusLoading, refreshStatus } = useBrandOnboarding();

  // Use debounced navigation to prevent glitches
  useDebouncedNavigation(
    status?.onboarding_status,
    isStatusLoading,
    [
      {
        condition: status?.onboarding_status === "company_info_added",
        path: "/brand-onboarding/brand-profile",
        delay: 600 // Longer delay for smoother transition
      },
      {
        condition: status?.onboarding_status === "brand_profile_completed",
        path: "/brand-onboarding/complete",
        delay: 600
      },
      {
        condition: status?.onboarding_status === "onboarding_complete",
        path: "/brand-dashboard",
        delay: 600
      }
    ]
  );

  const form = useForm<CompanyInfoFormValues>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      company_name: "",
      company_type: "direct",
      agency_name: "",
      website: "",
      industry_category: "",
    },
  });

  const companyType = form.watch("company_type");

  async function onSubmit(data: CompanyInfoFormValues) {
    setIsLoading(true);
    
    console.log("🚀 BrandOnboardingStep1: Form submission started", {
      timestamp: new Date().toISOString(),
      companyName: data.company_name,
      companyType: data.company_type
    });
    
    try {
      console.log("🌐 BrandOnboardingStep1: Making API call to setupCompanyInfo");
      await apiClient.setupCompanyInfo({
        company_name: data.company_name,
        company_type: data.company_type,
        agency_name: data.company_type === "agency" ? data.agency_name : undefined,
        website: data.website,
        industry_category: data.industry_category,
      });
      console.log("✅ BrandOnboardingStep1: setupCompanyInfo successful");

      toast.success("Company information saved!");
      
      // Add delay before refreshing status to prevent race conditions
      console.log("🔄 BrandOnboardingStep1: Refreshing status after delay");
      setTimeout(async () => {
        await refreshStatus();
        console.log("✅ BrandOnboardingStep1: Status refresh completed");
      }, 300);
      
      // Navigation is now handled by useDebouncedNavigation hook
      console.log("✅ BrandOnboardingStep1: Form submission completed, navigation handled by hook");
    } catch (error) {
      console.error("❌ BrandOnboardingStep1: Form submission failed", error);
      toast.error(error instanceof Error ? error.message : "Failed to save company info");
    } finally {
      setIsLoading(false);
      console.log("🏁 BrandOnboardingStep1: Form submission completed");
    }
  }

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">

      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

        <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Logo */}
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-48 h-48 object-contain relative z-10"
            />
          </div>

          {/* Header & Introduction */}
          <div className="text-center max-w-2xl mb-12 animate-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Hi, I'm Artie.
            </h1>
            <div className="space-y-4 text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed">
              <p>
                I help brands discover and partner with creators who can authentically represent their products and values.
              </p>
              <p>
                Whether you're looking for <span className="font-bold text-[oklch(0.2686_0_0)]">content creators, influencers, or storytellers</span>, you're in the right place.
              </p>
              <p>
                Let's start by learning about your brand so I can match you with the perfect creative partners.
              </p>
            </div>
          </div>

          {/* Primary Action Area (Form) */}
          <div className="w-full max-w-2xl bg-white shadow-xl border border-[oklch(0.92_0_0)] mb-8 overflow-hidden animate-in slide-in-from-bottom-4 duration-500 delay-200">
            <div className="p-8 md:p-10 flex flex-col">
              <h2 className="text-2xl font-bold mb-2 font-cabinet text-[oklch(0.2686_0_0)]">Ready to begin?</h2>
              <p className="text-[oklch(0.55_0_0)] mb-8 text-sm font-ibm">
                Tell us about your company so we can understand your brand and connect you with the right creators.
              </p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                
                <FormField
                  control={form.control}
                  name="company_type"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">I am signing up as...</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col sm:flex-row gap-4"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0 border border-[oklch(0.92_0_0)] rounded-xl p-4 cursor-pointer hover:bg-[oklch(0.97_0.01_60)] transition-colors w-full">
                            <FormControl>
                              <RadioGroupItem value="direct" />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer w-full text-[oklch(0.2686_0_0)]">
                              Direct Brand
                              <span className="block text-xs text-[oklch(0.55_0_0)] mt-1">I work directly for the brand</span>
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0 border border-[oklch(0.92_0_0)] rounded-xl p-4 cursor-pointer hover:bg-[oklch(0.97_0.01_60)] transition-colors w-full">
                            <FormControl>
                              <RadioGroupItem value="agency" />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer w-full text-[oklch(0.2686_0_0)]">
                              Agency
                              <span className="block text-xs text-[oklch(0.55_0_0)] mt-1">I represent a brand via an agency</span>
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="company_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Company / Brand Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Acme Corp"
                            {...field}
                            className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {companyType === "agency" && (
                    <FormField
                      control={form.control}
                      name="agency_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Agency Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Creative Agency LLC"
                              {...field}
                              className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Website</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://example.com"
                            {...field}
                            className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="industry_category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Industry Category</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]">
                              <SelectValue placeholder="Select an industry" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {INDUSTRIES.map((industry) => (
                              <SelectItem key={industry} value={industry}>
                                {industry}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

              </form>
            </Form>
            </div>
          </div>
        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              {/* Back button removed to prevent 404 error */}
            </div>

            {/* Progress Indicators */}
            <div className="flex gap-2 justify-center">
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={form.handleSubmit(onSubmit)}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Continue"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}